<script>
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiAuditsAPI from 'dashboard/api/aiAudits';

export default {
  components: {
    BaseSettingsHeader,
    SettingsLayout,
    NextButton,
  },
  data() {
    return {
      events: [],
      summary: {},
      availableEvents: [],
      filters: {
        conversation_id: '',
        event: '',
        q: '',
        limit: 100,
      },
      isLoading: false,
    };
  },
  computed: {
    noRecords() {
      return !this.events.length;
    },
    summaryItems() {
      return [
        ['TOTAL', this.summary.total || 0],
        ['CONVERSATIONS', this.summary.conversations || 0],
        ['APPROVAL', this.summary.requires_approval || 0],
        ['VENDOR', this.summary.vendor_issues || 0],
        ['COMPETITOR', this.summary.competitor_checks || 0],
        ['DRAFTS', this.summary.draft_ready || 0],
      ];
    },
    accountId() {
      return this.$route.params.accountId;
    },
  },
  mounted() {
    this.fetchAudits();
  },
  methods: {
    async fetchAudits() {
      this.isLoading = true;
      try {
        const params = Object.fromEntries(
          Object.entries(this.filters).filter(([, value]) => value !== '')
        );
        const { data } = await AiAuditsAPI.get(params);
        this.events = data.payload.events || [];
        this.summary = data.payload.summary || {};
        this.availableEvents = data.payload.available_events || [];
      } catch (error) {
        useAlert(this.$t('AI_AUDITS.ERRORS.LOAD'));
      } finally {
        this.isLoading = false;
      }
    },
    formatTime(value) {
      if (!value) return '-';
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    },
    conversationUrl(id) {
      return `/app/accounts/${this.accountId}/conversations/${id}`;
    },
    payloadMessage(payload = {}) {
      return (
        payload.message ||
        payload.customer_safe_reply ||
        payload.latest_customer ||
        payload.private_note ||
        payload.detail ||
        payload.status ||
        JSON.stringify(payload).slice(0, 240)
      );
    },
    payloadLabels(payload = {}) {
      return payload.labels_applied || payload.labels || [];
    },
    payloadMeta(payload = {}) {
      const parts = [];
      if (payload.vendor_issue) parts.push('vendor issue');
      if (payload.requires_approval) parts.push('needs approval');
      if (payload.reason) parts.push(payload.reason);
      if (payload.agent_action) parts.push(payload.agent_action);
      return parts.join(' / ');
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AI_AUDITS.LOADING')"
    :no-records-message="$t('AI_AUDITS.EMPTY')"
    :no-records-found="noRecords"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AI_AUDITS.TITLE')"
        :description="$t('AI_AUDITS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="space-y-5">
        <div class="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <div
            v-for="[key, value] in summaryItems"
            :key="key"
            class="rounded-md border border-n-weak px-4 py-3"
          >
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ $t(`AI_AUDITS.SUMMARY.${key}`) }}
            </div>
            <div class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ value }}
            </div>
          </div>
        </div>

        <form
          class="grid items-end gap-3 md:grid-cols-[10rem_14rem_minmax(0,1fr)_7rem_auto]"
          @submit.prevent="fetchAudits"
        >
          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">
              {{ $t('AI_AUDITS.FILTERS.CONVERSATION') }}
            </span>
            <input
              v-model="filters.conversation_id"
              class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
              inputmode="numeric"
            />
          </label>

          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">
              {{ $t('AI_AUDITS.FILTERS.EVENT') }}
            </span>
            <select
              v-model="filters.event"
              class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
            >
              <option value="">{{ $t('AI_AUDITS.FILTERS.ALL') }}</option>
              <option
                v-for="eventName in availableEvents"
                :key="eventName"
                :value="eventName"
              >
                {{ eventName }}
              </option>
            </select>
          </label>

          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">
              {{ $t('AI_AUDITS.FILTERS.SEARCH') }}
            </span>
            <input
              v-model="filters.q"
              class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
            />
          </label>

          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">
              {{ $t('AI_AUDITS.FILTERS.LIMIT') }}
            </span>
            <input
              v-model.number="filters.limit"
              class="w-full rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
              min="1"
              max="200"
              type="number"
            />
          </label>

          <NextButton
            type="submit"
            icon="i-lucide-refresh-cw"
            :label="$t('AI_AUDITS.REFRESH')"
            :is-loading="isLoading"
          />
        </form>

        <div v-if="events.length" class="min-w-full overflow-x-auto">
          <table class="min-w-full divide-y divide-n-weak">
            <thead>
              <tr>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.TIME') }}
                </th>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.CONVERSATION') }}
                </th>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.EVENT') }}
                </th>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.MODEL') }}
                </th>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.MESSAGE') }}
                </th>
                <th class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold">
                  {{ $t('AI_AUDITS.TABLE.LABELS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak text-sm text-n-slate-11">
              <tr v-for="event in events" :key="event.id">
                <td class="w-44 py-4 ltr:pr-4 rtl:pl-4 whitespace-nowrap">
                  {{ formatTime(event.ts) }}
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  <a
                    class="text-n-brand hover:underline"
                    :href="conversationUrl(event.conversation_id)"
                  >
                    #{{ event.conversation_id }}
                  </a>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  <div class="font-medium text-n-slate-12">
                    {{ event.event }}
                  </div>
                  <div class="mt-1 text-xs text-n-slate-10">
                    {{ payloadMeta(event.payload) }}
                  </div>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  {{ event.model || '-' }}
                </td>
                <td class="max-w-xl py-4 ltr:pr-4 rtl:pl-4">
                  <div class="whitespace-pre-wrap break-words text-n-slate-12">
                    {{ payloadMessage(event.payload) }}
                  </div>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4">
                  <div class="flex max-w-sm flex-wrap gap-1">
                    <span
                      v-for="label in payloadLabels(event.payload)"
                      :key="label"
                      class="rounded border border-n-weak px-2 py-1 text-xs"
                    >
                      {{ label }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
